/* eslint-disable no-unused-vars */
import React, {
  useState, useEffect, useRef, useLayoutEffect,
} from 'react';
import clsx from 'clsx';
import {
  EVSvgIcon,
  EVTooltip,
  EVList,
  EVListItem,
  EVListItemIcon,
  EVTypography,
  EVIconButton,
} from '@eagleview/ev-comp-library';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import ChevronTopRoundIcon from './ChevronTopRound.Icon';
import useStyles from './PrimaryNavBarStyle';
import { HIDDEN_APPS } from '../../constants/AppConstants';
/**
 * PrimaryNavBar Component - renders list of applications
 */
const PrimaryNavBar = (props) => {
  const {
    apps, defaultAppId, handleNavChange, userDetails, show, shouldHeaderRender,
  } = props;

  const pathname = (window.location && window.location.pathname) || '/';
  const appIdInUrl = pathname.split('/')[1] || '';
  const [selectedApp, setSelectedApp] = useState(appIdInUrl);
  const [showScrollButton, setShowScrollButton] = React.useState(true);
  const [onSideNavHover, setOnSideNavHover] = useState(false);
  const [scrollToBottom, setScrollToBottom] = useState(true);
  const classes = useStyles({ show });
  const listContentRef = useRef();
  const scrollRef = useRef();

  useEffect(() => {
    if (!isEmpty(userDetails)) {
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
    }
  }, [userDetails]);

  useLayoutEffect(() => {
    if (!listContentRef.current) return;
    if (listContentRef.current.offsetHeight >= listContentRef.current.scrollHeight) { setShowScrollButton(false); }
  });
  /**
   * called when user clicks on one of the applications.
   * updates selected appId to state and calls handleNavChange function passed through props
   * @param {Object} app - application selected
   */
  const handleClick = (app) => {
    const appId = app.appId || app;
    if (app.externalLink) {
      window.open(app.rootUrl, '_blank');
    } else {
      setSelectedApp(appId);
      // eslint-disable-next-line no-unused-expressions
      handleNavChange && handleNavChange(appId);
    }
  };

  /**
   * generates svg html element from svg string
   * @param {Object} app - application selected
   * @param {String} app.icon - svg icon in string format
   * @param {String} appSelected - currently selected application id
   */
  const getSVG = (app, appSelected) => {
    const svgIcon = app.icon;
    try {
      const innerHtml = new DOMParser().parseFromString(svgIcon, 'text/html').body.firstChild.innerHTML;
      return (
        <svg
          className={clsx({ [classes.appIcon]: true }, { [classes.appIconSelected]: app.appId === appSelected })}
          dangerouslySetInnerHTML={{ __html: innerHtml }}
        />
      );
    } catch (e) { return null; }
  };

  const handleScrollIconClick = () => {
    if (scrollToBottom) {
      setScrollToBottom(!scrollToBottom);
      listContentRef.current.scroll({
        top: listContentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    } else {
      setScrollToBottom(!scrollToBottom);
      listContentRef.current.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (e.target.scrollTop === 0) {
      setScrollToBottom(true);
    }
    if (bottom) {
      setScrollToBottom(false);
    }
  };

  const handleScrollAppList = (e) => {
    if (/Firefox[/\s](\d+\.\d+)/.test(navigator.userAgent)) return;
    // using timeout, adding and removing relevant classes to show/hide scrollbar
    e.persist();
    if (e.target && e.target.classList) {
      e.target.classList.remove(classes.hideScrollBar);
    }
    clearTimeout(scrollRef.current);
    scrollRef.current = setTimeout(() => {
      if (e && e.target) {
        e.target.classList.add(classes.hideScrollBar);
      }
    }, 1000);
  };

  const filteredApps = apps.filter((app) => !HIDDEN_APPS.includes(app.appId));
  // apps.length = 2;
  // calls handleClick function to set default application when none selected
  if (defaultAppId && selectedApp === '') handleClick(defaultAppId);
  return (
    <div
      className={
        clsx(
          { [classes.drawer]: true },
          { [classes.drawerClose]: true },
          { [classes.navbarHeightWithHeader]: shouldHeaderRender },
          { [classes.navbarHeightWithoutHeader]: !shouldHeaderRender },
        )
      }
      onMouseEnter={() => {
        setOnSideNavHover(true);
      }}
      onMouseLeave={() => {
        setOnSideNavHover(false);
      }}
      onScroll={(e) => handleScroll(e)}
    >
      <div
        className={
          clsx(
            classes.listContent,
            classes.showSrollBar,
            classes.hideScrollBar,
          )
        }
        ref={listContentRef}
        onScroll={(e) => handleScrollAppList(e)}
        id="list-scroll"
      >
        <EVList
          classes={{ padding: classes.removeListPadding }}
          id="navBarList"
        >
          {filteredApps.map((app) => (

            <EVTooltip id={`listItemToolTip-${app.appId}`} title={app.tooltip}>
              <div>
                <EVListItem
                  id={`listItem-${app.appId}`}
                  onClick={() => handleClick(app)}
                  key={app.appId}
                  className={clsx(
                    classes.listStyles, { [classes.selectedAppStyle]: (app.appId === selectedApp) },
                  )}
                >
                  <EVListItemIcon className={classes.listItemIcon}>
                    <EVSvgIcon component={() => getSVG(app, selectedApp)} viewBox="0 0 244 244" />
                  </EVListItemIcon>
                  <EVTypography className={classes.listItemText}>
                    {app.name}
                  </EVTypography>
                </EVListItem>
              </div>
            </EVTooltip>
          ))}
          {/* Adding dummy app to give extra space to scroll up for last app in list */}
          <EVListItem className={classes.dummyApp} />
        </EVList>
      </div>
      {onSideNavHover && showScrollButton && (
        <div className={classes.scrolToTopButton}>
          <EVIconButton
            className={clsx({ [classes.scrollToBottom]: scrollToBottom }, { [classes.scrollToTop]: !scrollToBottom })}
            id="chevronScrollIconButton"
            onClick={handleScrollIconClick}
          >
            <EVSvgIcon
              component={ChevronTopRoundIcon}
              viewBox="0 0 244 244"
            />
          </EVIconButton>
        </div>
      )}
    </div>
  );
};

PrimaryNavBar.propTypes = {
  /**
   * The list of applications that are rendered.
   * @param {{appId: String, icon: String, tooltip: String, name: String, order: Number}[]} apps
   */
  apps: PropTypes.arrayOf(PropTypes.shape({
    appId: PropTypes.string,
    icon: PropTypes.string,
    tooltip: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
  })).isRequired,

  /**
   * This handler will be called every time an application is selected.
   * @param {String} appId - The selected application id.
   * @property {Function} handleNavChange
   */
  handleNavChange: PropTypes.func,

  /**
   * default application id
   * @param {String} defaultAppId
   */
  defaultAppId: PropTypes.string,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * show/hide
   * @property {Object} userDetails
   */
  show: PropTypes.bool.isRequired,

  /**
   * show/hide
   * @property {Boolean} shouldHeaderRender
   */
  shouldHeaderRender: PropTypes.bool.isRequired,

};

PrimaryNavBar.defaultProps = {
  defaultAppId: '',
  handleNavChange: undefined,
};

export default PrimaryNavBar;
